import { useState, useEffect, Fragment } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { fetchPublicForm, fetchPhoneCode } from "../api/queries";
import { submitPublicForm } from "../api/mutations";
import Select from "react-select";
import {
  COUNTRY_OPTIONS,
  customStyles,
  filterCountryOption,
  getCountryName,
} from "../constants/countries";
import { useLanguage } from "../contexts/LanguageContext";
import { Transition, Dialog } from "@headlessui/react";
import LanguageSelector from "../components/LanguageSelector";
import { openExternalUrl } from "../utils/url";
import { isHiddenFieldType } from "../constants/fieldTypes";

export default function PublicForm() {
  const { formId } = useParams();
  const [formData, setFormData] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [defaultCountry, setDefaultCountry] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPhoneFieldReady, setIsPhoneFieldReady] = useState(false);
  const [acceptedTos, setAcceptedTos] = useState(false);
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);

  const fieldOrder = ["name", "email", "phone", "birthday"];
  const HIDDEN_VALUE_MAX_LENGTH = 255;

  const getDefaultTermsOfService = (storeName, email) => {
    return t("passes.form.termsOfService.defaultTerms", {
      storeName,
      email,
    });
  };

  const [searchParams] = useSearchParams();
  const referrerUUID = searchParams.get("r");

  const { t, language, setLanguage } = useLanguage();

  // Add redirect effect for specific formId
  useEffect(() => {
    if (formId === "af383637") {
      window.location.href = "https://club.ciaonapoli.de/join";
    }
  }, [formId]);

  const { data: form, isLoading } = useQuery({
    queryKey: ["publicForm", formId],
    queryFn: () => fetchPublicForm(formId),
    cacheTime: 0,
    staleTime: 0,
    persister: false,
  });

  // Follow the form's configured language, like app-public-form does.
  // Without this the page chrome (labels, validation, the success screen)
  // renders in whatever language the visitor's browser/localStorage last
  // used, while the merchant-authored form content is in the form's
  // language — a mixed-language page for the customer. Keyed on the lang
  // value, so a manual pick from the selector still wins afterwards.
  useEffect(() => {
    if (form?.lang) {
      setLanguage(form.lang);
    }
  }, [form?.lang, setLanguage]);

  // Separate query for phone code
  const { data: countryData, isLoading: isCountryLoading } = useQuery({
    queryKey: ["phoneCode", form?.extras?.store_id],
    queryFn: async () => {
      const response = await fetchPhoneCode(form?.extras?.store_id);
      return response;
    },
    cacheTime: 0,
    staleTime: 0,
    persister: false,
    retry: 3,
    enabled: !isLoading,
    onError: (error) => {
      console.error("Phone code fetch error:", error);
    },
  });

  // Add effect to monitor data changes
  useEffect(() => {
    if (!isCountryLoading && countryData) {
      const phoneCode = countryData.phone_code
        ? `+${countryData.phone_code}`
        : null;
      const countryCode = countryData.country_code;

      if (phoneCode && countryCode) {
        let detectedCountry;

        if (countryData.phone_code === "1") {
          // For +1 countries, use the special format
          detectedCountry = COUNTRY_OPTIONS.find(
            (option) => option.value === `+1 ${countryCode}`,
          );
        } else {
          // For other countries, just match the phone code
          detectedCountry = COUNTRY_OPTIONS.find(
            (option) => option.value === phoneCode,
          );
        }

        if (detectedCountry) {
          setDefaultCountry(detectedCountry);
          setFormData((prev) => ({
            ...prev,
            phone_country_code: phoneCode,
          }));
          setIsPhoneFieldReady(true);
        }
      }
    }
  }, [countryData, isCountryLoading]);

  // Pre-fill form fields from query parameters
  useEffect(() => {
    if (!form?.content?.fields) return;

    const reservedParams = ["r"];
    const prefillData = {};

    for (const [key, value] of searchParams.entries()) {
      if (reservedParams.includes(key)) continue;
      const matchingField = form.content.fields.find((f) => f.field === key);
      if (!matchingField) continue;

      if (matchingField.type === "checkbox") {
        prefillData[key] = value.split(",").map((v) => v.trim());
      } else if (isHiddenFieldType(matchingField.type)) {
        // Nobody reviews what arrives here — a hidden field is filled straight
        // from the URL and stored verbatim in the customer's custom fields, so
        // a hand-built link would otherwise push an essay into the record.
        // Long enough for a uuid, a click id or a signed token.
        prefillData[key] = value.trim().slice(0, HIDDEN_VALUE_MAX_LENGTH);
      } else {
        prefillData[key] = value;
      }
    }

    if (Object.keys(prefillData).length > 0) {
      setFormData((prev) => ({ ...prefillData, ...prev }));
    }
  }, [form, searchParams]);

  const submitMutation = useMutation({
    mutationFn: submitPublicForm,
    onSuccess: (data) => {
      setError(null);
      setSubmitted(true);

      // Handle wallet redirect here
      const deviceType = getMobileOperatingSystem();
      console.log("Device type:", deviceType);
      console.log("Mutation data:", data);

      if (
        data?.pkpass_url?.apple_wallet_url ||
        data?.pkpass_url?.google_wallet_url
      ) {
        // Show success state for 1 second before redirect
        setTimeout(() => {
          if (deviceType === "iOS") {
            window.location.href = data.pkpass_url.apple_wallet_url;
            return;
          }

          if (deviceType === "Android") {
            window.location.href = data.pkpass_url.google_wallet_url;
            return;
          }
        }, 1000);
      }
    },
    onError: (error) => {
      setError(
        error.response?.data?.error ||
          t("public.form.unexpectedError"),
      );
    },
  });

  function getMobileOperatingSystem() {
    var userAgent = navigator.userAgent || navigator.vendor || window.opera;
    console.log(userAgent);

    // Windows Phone must come first because its UA also contains "Android"
    if (/windows phone/i.test(userAgent)) {
      return "Windows Phone";
    }

    if (/android/i.test(userAgent)) {
      return "Android";
    }

    // iOS detection from: http://stackoverflow.com/a/9039885/177710
    if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
      return "iOS";
    }

    return "unknown";
  }

  const handleSubmit = async (e) => {
    setError(null);
    e.preventDefault();
    if (submitMutation.isPending || isSubmitting) return;

    // Validate all required fields. A hidden field has no input to fill, and
    // most visitors arrive with no parameter at all, so enforcing `required`
    // on one would block them from signing up at all.
    const requiredFields = form.content?.fields?.filter(
      (field) => field.required && !isHiddenFieldType(field.type),
    );

    const hasEmptyRequired = requiredFields?.some((field) => {
      const fieldValue = formData[field.field];
      if (field.type === "checkbox") {
        return !fieldValue?.length;
      }
      return !fieldValue;
    });

    if (hasEmptyRequired) {
      setError(t("public.form.requiredFields"));
      return;
    }

    setIsSubmitting(true);
    await submitMutation.mutateAsync({
      formId,
      data: {
        ...formData,
        referrer_uuid: referrerUUID,
      },
    });
    setIsSubmitting(false);
  };

  const handleInputChange = (field, value, countryOption = null) => {
    setError(null);
    if (field.type === "tel") {
      setDefaultCountry(countryOption);

      setFormData((prev) => ({
        ...prev,
        [field.field]: value,
        phone_country_code: countryOption
          ? countryOption.value
          : prev.phone_country_code,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [field.field]: value,
      }));
    }
  };

  if (isLoading) {
    return (
      <div className="bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">{t("public.form.loading")}</div>
      </div>
    );
  }

  if (!form) {
    return (
      <div className="bg-gray-50 flex items-center justify-center">
        <div className="text-red-500">{t("public.form.notFound")}</div>
      </div>
    );
  }

  if (submitted) {
    return (
      <>
        <div
          className="min-h-full flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8"
          style={{ backgroundColor: form.background_color, lineHeight: "1.15" }}
        >
          <div className="absolute top-4 right-4">
            <LanguageSelector textColor={form.text_color} />
          </div>
          <div className="max-w-md w-full space-y-8 text-center">
            {form.extras?.store_logo_url && (
              <img
                src={form.extras.store_logo_url}
                alt={form.extras?.store_name}
                className="h-32 w-auto mx-auto"
              />
            )}
            <h2
              className="text-3xl font-bold"
              style={{ color: form.text_color }}
            >
              {t("forms.edit.preview.success.title")}
            </h2>
            <p className="" style={{ color: form.text_color }}>
              {t("forms.new.preview.success.description_two")}
            </p>
            <p className="text-l mt-2" style={{ color: form.text_color }}>
              {submitMutation.data.message}
            </p>
            {submitMutation.data.pkpass_url.apple_wallet_url && (
              <div className="pt-4">
                <div
                  className="text-sm text-center mb-3"
                  style={{ color: form.text_color }}
                >
                  {t("forms.new.preview.success.description")}
                </div>
                <div className="flex items-center justify-center gap-4">
                  {/* iOS Wallet Button */}
                  <a href={submitMutation.data.pkpass_url.apple_wallet_url}>
                    <img
                      src="data:image/jpeg;base64,iVBORw0KGgoAAAANSUhEUgAAAMgAAAA3CAYAAABJnAVSAAAAAXNSR0IB2cksfwAAAAlwSFlzAAALEwAACxMBAJqcGAAAD3BJREFUeJztXQl4TdcWPqUjpeakOplqVjE9raFCSwf0K1K0NGosUcPTFp9niA8x1VNDkbSV2Y0gISSGR0wviZDEEHNMpaaawqvSIuvtf8u+PffmDueS3HNvstf3/V9yz97n7H3OXf/Za6299r6Kko8SFhZWKTIycsiyZcsMDEkGgyGL/f0fA0lI5CPuMVxg2M+wgWFqREREy/zU5XyV6OjocowYi0NCQu4OGzaM2rZtS7Vr16aXXnqJSpUqRU8//TSxahISj40nnniCSpYsSRUqVKAaNWpQ06ZN6dNPP6W5c+eCOPsY2imuJIwY/2DsvdivXz9OBsUFHqJE0QOI07x5c/rhhx9yGElm+fv7F1P0FmZCNQ8ODr7drFkz3R+QhASAlzQjB0aTIEVPCQ0NLc9Gj0sY4ix1VEJCLzz33HM0c+ZMYi/wwYpeAp8DZpWWDktIOBuvvPIKhYeHZyNwpDhbmFNekY0gd0qXLq37g5CQsIYvv/wSo8h4xdmCUC6iVflxExISBYVatWrBFzmkOFsYQSLbtWun+wOQkLCF4sWLU3BwcA78ZcWZwliZVKdOHd0fgISEPcBZZ+/zJoozhRHkROXKlXW/eQkJexg3bhz8kI6KM4UR5Nbzzz/vcGfr1a1NXbt0ph7du9KA/r40cEAfp6Mna7tb14+oSWMvKlasmO5foETBYujQoSBIP8WZwghy/6mnntLcyZIlS1BYyBI6djjNpbAubjl5elRy6IEjzaFJkyZ85tZeXfhp+IKslXt6emLmV3clKswYNGgQHHU/xZmChDE4QFo7Odl/HFfIxIwM+ndiJgVs1hezt2RS/J69vE+hwYsdeuDDhw8nSOvWre3WHTFiBMXFxVktR77an3/+abW8R48e1L17d92VzJ0xYMAAjCBDFWeKIwSBGZOxZwdl7N9D7Q1HyTvymEugLcPOvRmcJBUqlNf8wNPS0mjXrl30008/FThB5s2bx5PwtPZNIi/69++PEWSY4kxxhCClS5fiSrhxV6rupDDH8uT9vG/wjbTcS926denatWuECF52djaVKFHCpBwm1datWykrK4sr9+jRo00I8vLLL1N0dDQdP36cH//ggw+sEmT+/Pl0+fJlDpDy1Vdf5cerV6/Or3H06FHeVpcuXXRXQlcGsj0iIyNH2NbofBZHCFKuXFmuhAkpqdRh2QEaYwik2cv8GSbpBH+aELWAOkamUWQuQbwaNtB0LwgZLl780CRLTU3ladaiDKPB3bt3adSoUVS/fn36+uuvuXILgjz55JNcqcPDw6lhw4b08ccf08GDB60SBKnczDTg6x/g8zz77LOEzIVLly7R9OnTqUGDBtwEu379OnXq1El3RXRVuA1BNqQkU9aqukSri7sErsRUplVJ/+V9a+T1ht37gKl47tw5evvtt/nnkSNH0vr1643lMIXwZlefExUVZSQIRgsQ5plnnjGWY/h3xMQaMmQI7dmzx6TO2LFjacuWLboroqvCbQiSkrxSd1KYI3XnIs0E6dChAx8hBg8ezCMjEydOpPv375OYD1q3bh2NGTPG5ByMIoIgSM1JTEw0Kcco4AhB8H9QUJBJnfbt29Ovv/6quyK6KtyGIGnJ4Vwpc2KL0/0Y/fAg9m+C7NsxSzNBIiIiKDk5mQIDA42AL/HNN9/wcjyTqVOnmpwzefJkI0H69OmT5+3fsmVLhwiSu87BpE63bt24qabluyiKcBuCpDOChG+qSp2SWpF3ireueC+5Nc3dUkszQWD73759mxo3bmxyHCaSUE7Mdxw7dszouGO+BJ8FQWrWrEl//fUXvfHG3239/PPPNgkyZ84ck2gZljLD56hU6eHcDeZiVq9eLedSCgNBtu1aqjsxzBGXPFUTQfCQjxw5kud4mTJluNnVqFEj7kQnJSVxUgQHB1NmZib3SdRRrGnTpnEFh6O+fft2/tcWQT755BO6c+cOr/fiiy/yY4sWLaLz58/zNnbv3s0J6uHhobsiuirchiAJqUGmCpqsE1R9CEudrIkgWFJsbeXku+++S6+//jr/H5tTdOzYkTvTiEJVqVKFm1Hq+lg3DT+mTZs2fOUbTCR7bX/22Wcm6/1xDH4Q2gIxtXwPRRVuQ5B1qYHku749RSzsQgmzfCh+RjeKn+5EsPYSZvrwtpfP70p+ce9RaKq/Zh9Ewj3hNgTZmBxEl0f50q1hfVwC2cP7UOzWKZIghRxuQ5CUzUG6k8IcybHTJUEKOVyeIB4eFen4kXRK326gyzP+ZR8BY+jamCF04ytfyma46YDCo+610YPpt2ljNbWVlhBIRw/uplYtmuv+RUoUUYJUr16VTmVlOIbj6XRqaxydGzecfvPrzYlilxjjhtGZTTEPz3WwPawRceShlytXjjvnCLs6ct7jAKnxyPWqVq3aY10HgQH03VI2MoIMKHvrrbfylCF8be08a0D6DfqM54XP0Bl8RqDCWc/N5Qni6enhOEFycTIzmU6M9qNLg3vZJMnVsX50au/OR26nrXcrhx76pEmTSIh6XqMg4evry9tDHtbjXAc5YTdv3qQHDx7wMLW6DMoLQega8zjqMuR7QTZs2KC5LczzQN5//33+GYvsIAiFO9pv5LeBoOXLa8+8VtyBIPBBHlVxgRO7/kNZQ3rzkcSSuXVjRF86vSP+sdpo3Ei7kmNyDjPoSDOBzJ49260IAqxdu5ZfC/lh6uO4thCk1qjLkKgJQfqM1nbykyDQOUt9todCTxAga0ssnRr2BV3JNbdu5ppV1//Zn85sWPHY13eEIDA/IMiJwoQd4MjisUdFfhIEeWGWroW0ekH87777zqQMk6AQZCJrbUcSxEkEeWhuJdHJiMV0do4/nZ8zic6FL3oss+pRCYKZbAj8D6zZgCBhUF0HJIKyTZgwgSvHvn37+Iz5iRMneBawernuwoULed169erx3KurV6/SH3/8wU0ZHBP1LBEEGcZ4o0PhoNgXL17k/StbtqzNe8B6FsjOnTuNx2C6wOxasWIFXbhwgfbv328sg9+C/iPVXvQdE5RI2ETbMMnOnj1LP/74o3HGX3GAIJ9//jl/RkjFQaYBU2jj+hcgJSWFr8OBYK0NnpdW/SsyBClIaCUInNgrV65wRcT9Y5YcEhYWZlIPCgFBciKUB+noCQkJ/H8IdtoQdTdu3MiPQQmgmCtXruRpLRAoBRZZKVYIIsgKk2/JkiW0bds2/jkjI8PuDDsUGv0R9bC2BNK3b18KDQ2lnJwco7J7e3vzMqS8iPORvAnZu3cvbxv9F22L/Qq0EATrZyC4d4zKMP/QNjKURc4Z9O2XX37h9Xbs2MFTeCRBXJAgWL0HwciBz3ibnjlzhn7//XdS7/IiCAJBmog4jvQSvKVRX6SOCIIcOnTIGO2BI403OeT777+3SBCYOpD09HQTMgjSYNtNW/eCXC6IiEohKRKKifT9nj178rJevXrxMoyEEPQBn9F3kDI+Pt6oB3gWgiRY4KVoIAh+4wOjJcxU/C/6Jkij9u+kieUGBImJieFfkjq/Chm3kN69e+chiNpMEdi0aRMvE06wIAjS4dX1ELWBiGxhc4KMHz+ef4YywaQSgMJDYmNjbd4LiKsezU6fPs1HA/yP68BkCwkJMemjGM0sARExjDAQrJZUNBBEEHHBggUm9wDzCu0j4VNcXxLExQmCtztMEowAsLXFmhChPPgr6gqCmK/dAOBnCFNGUSmfeTIkzBS0dePGDYsEgTliS7Ak2Nb9wHzBiIFVkUjFhyDbWJRj7Qve7BjNbt26lWfNyZtvvklr1qzhppC5aCUI1uzbEpiYoj1JEBcnCDJwbQmUGT85p6gIAnva/DpLly7lZWI9uyCI+V7HeCND4BgrFgiCty4Ei7IwP2AOLZNxcIwxJyK2MmrV6u/5ILVZBVEv3MLohjR8ONRw1OG/YETEqOUIQRCwgMC8s3QP8H1Em4WWIGXLltGdAPbg1bC+3fsQYU5Ls8zmcwSCIHj7v/DCCyajAkwZiJhgFAQxD6tiLQhk8+bNFgkycOBA/hltq89DezgXb3h79zRr1ix+jZMnT/K+YrQQZRjRIIgaQT788ENjGZYWq+9XQJiPWgmCSCBk1apVJteBbvn4+HCSiGOFliDYpODkI6R/OBPVq1WxeQ/YagfmCCI/lnZUhFMKwRtZURHk3r17XGlABmzFr478iHMFQbBiEasSYedjnQeiOhDhm5gTBCMMTBCch9EIoViMYFA2iDDhbAFvfSHmG04ghIzRC4IQrzoIgZWUEDjpICTSUOALYRR1hCDQocOHD/Nniz3EsHITpqwYHQMCAoxtimPYpEKL3qn7ajAYhuen/tsVR3dWXBMToTsJrCF9d6Lde8FacAjeuNbqIKojRgZBEGzbg5i9WhAm9vLyykMQOMtQRLUsX77cuH+wpTDvO++8w/fnUguUDWFXLVujIvqFKJI1QiF8DcH+W+rjSEM5cOCAsT0QA4qO/jpCEADmGnaLMReQTx2dw2giBGaherSzBeysyEaQr/JL9zUJI0iO1g4q/A3rRYczU3QngzlOHEujjzrbH7I7d+7MV/C99tprVuvAXkYdfOGCILCt8SVjFEDIEm/JihUrmpwnCILzMMogOgWzCaOIuh5WLcKsMv/BVIRH4R/h+t9++60xxKoVWNGIfosQsxroE8osmZXivuDYw4wBaRBBQ32spEQdfEafRfQLOoPPMAHV18K5eAHgHvCisJYE2qJFC+5zoZ7WjcfRnh578951dKlnzZo1aEbARIqLjaRtiWt1RfzaKJo3N4CaNW3k0D1ohZog9uqqCVIQfSnqyP0ZNuf+mCcjyHn1xI6EKSRBXAcYVRlBfBRnCiNIBhzXgrihwoCqVavySI+WiAt2b0dd+cIpGEyZMgUmVivFmcKcngT5++gS7gAkg4aHh9dQnCmMkVPVGzdLSLgiEHhgunrd39+/mOJMiYiIaImZVS2hRAkJvYDJTUYQg6KHsIb3OXNtsYSEI8DSBOS9MT1to+ghaJjZdznqHf8kJFwFyFZmOhqr6CnMWZ+NWWakOVjqpISEHsCsOyPHmejoaE9FT4HzYzAYAjHzq14iKSGhB2BWYZEXyBEVFVVHcRXBTCVz3LMxa4m1Bc7YxEBCQgDRKjjkuT5HDIOH4moSFhZWiRFlPOvcYSSpzZgxg2de+vn58YQxCYn8AvKrkGqPvCwsf2Y6d2MZwlUGQ2u9eaBJWF8rsBGlGetwR+anfMEwCMBIw/6OkZBwFEynRkbm6lHkQ13yASGYKVWT+RrF9dZ5KVKKpPwfx47Kd0yWmfkAAAAASUVORK5CYII="
                      alt="Add to Apple Wallet"
                      className="h-12 cursor-pointer hover:opacity-80 transition-opacity"
                    />
                  </a>
                  {/* Android Wallet Button */}
                  <a href={submitMutation.data.pkpass_url.google_wallet_url}>
                    <img
                      src="data:image/jpeg;base64,iVBORw0KGgoAAAANSUhEUgAAAMgAAAA3CAYAAABJnAVSAAAAAXNSR0IB2cksfwAAAAlwSFlzAAALEwAACxMBAJqcGAAAEixJREFUeJztnQlwVFUWhi1nxqlZqqbKcaupmpJNICAJkEDIwiIgQkAIOwqEHVQERTbZAwkiSwmCKCAQDAkJQUAKZXdDQBRFXBAERkEEAWVJArLqmfvd5DYvne6kO4R+neK9qr+S9Hv91vPdc869577cdlspLqmpqfekp6c/uWzZsgyl7RkZGQfVz1wlceSoFHVV6bjSl0oblJLT0tJiStOWS3XJysq6U4Hx2pIlSy4NGjRIIiIi5P7773fk6KbrgQcekMcee0xmzpwJOHuUGtvNQ4FFgVFX0ftz7969bb9Zjm5tVa1aVebOnfuHgmRaYmLi7XazcZsKoSJTUlIuVK5c2fab48iRkYIDb7LAVjjeeOONfyvvcQIXZ/cNceTIXVOnThXVgD9hGyDkHE5Y5SiYtXTp0nN0HAUcDpWU3608yEW7b4AjR0VpwIABeJGxAQeErlx6q+y+AY4cFSeVi+y1A5D0unXr2n7xjhwVp5SUlD/IlwMKiKJyu90X7siRLyJZV+15eKABOWT3hTty5ItGjx5NHtIy0IDk+HOSD8SFSUivehLSJ+qmq0p8bSlftUKp3eDmzZvTGyKvv/66REVFFblteHi4zJs3TyeHntaHhYXJnDlzZPDgwTfVKBxd18CBAwGkd6ABuebLyVXpEC615rWSiIx2EpHZPqCqMbKRlKtU/oZv8LRp0+TUqVNy6NAhSUhIKHLb+Ph42bZtm0yYMMHj+ri4OHnvvffkxRdfLLSuf//+snHjRhk6dOhNNZhbTdxXZa9PBRqQYk8spGtdCV8SH3AwShOSihUryoYNG7T27t0rr776qrgPjIaEhMjjjz+uPU23bt0KAVKlShXp0qWLtGrVStcMeQKkfPnyMmbMGPn444/1Ousx+L179+76QeOB7Da4sqa+ffviQQYGFSCEVOGL29gKh1FIQmSJb+6oUaPkiy++0CETYda7774rrVu3dq1noPSrr74Ss3z//ffy888/uwAhlDpw4IBr/YkTJ+TXX38tBMjw4cPl6NGjru02bdokTZs2lT59+sjXX3/t+jwnJ0dDClB2G15ZEfdQ2eugoAKkxujGEp4W+LDKk2rOipMKYSUrh1m0aJG8//770q5dO+nXr5989tln8vzzz+t1fEaLv2XLFnn00Ud16872ubm5GhBA2rp1qw6bWrZsKaGhodq4MXJPIdawYcPkgw8+kIkTJ+pq6DZt2sj69etl9erVUr9+fXnwwQd1j8y3336rvY3dhldWRCOWnp7+TFABUnN2S5V3tJeGy9vIjDU1Zf/GO+XSlr/I1S1/vuk6vPFfkrI2RDquaCb1MttJeGpbqRgV4veNJWTauXOnnn/Qq1cvGTJkiPYmS5YskerVq8vTTz+t4XniiSdc37HmICNGjJAPP/xQnnzySdf6Dh06yPbt233KQTgef1v3T5i2efNmWbBgge2GV1YUlIDUnv+oxK2Ikz3r7w4YGO46uekfMmJ1lMQsj5dKsdX8vrF0Dx45ckTcF5L1nj176hYfGKwG3KhRI52vGEAwcFy8WY+n8TVJZx8ABpxmm2bNmsnatWs1pHYbXllR0AKye/09tsFhhWTwqlip0sA/QEjOyQMIcUisCW/QM888I998842MHz9eQwIgycnJUq1a3v5p9QEI4+7Ro4fs2LFDZsyYob/Leroc9+/f7xUQQizA5G8SfjwU3zfTCTgmHoieNbsNr6woKAEZtiRcTm/+u61wGK19p7zENPZvXKRTp06ye/dunQ+4ryMnAJ727dvrsRGS608//VS39j/88INcvnzZlaQz5sF6QOI7JPGs9wRI27ZtNSBsrx6oBgw4+PuTTz7RngmPhpeJiYmx3fDKioISkK2Zd8nFLXfYDgfat+FOiW/5X79uKoAQ6njqVqVlBxyMtEKFCjqUWrVqlQ578AJ4EdPTxfrnnntO3nzzTUlNTdXr6LECLk/Hbdy4sZ46OmnSJJ3U01v11FNPyZo1a3RoxbHoNrbb6MqSghKQoyv+qo3zyjt/ldzJ/5HTXWrIqYcj5FSTOjddv3YIlewx98ulzH/K1U15IV5Cq3ttf1CO7FHQAnJ59d/kTPfqAQPDXb+0riUXXrlLrmy4wwHkFlbQAnImoZptcFgh+W3enZIQd5/tD8qRPQpKQAZPXCgL+s6Q7W0T5HjTKNsAOdiimUwftlgiouNsf1CO7FFQAhI18qDUn5QjDZJypEXiCek/YqfM7LdANnYaqI32ZNPImw7HrjZdZNDwbfJwcraExHaz/UE5skdBCUg9BUhsUq7EKkjqJwFKrjRUajT5vDyUnCvdx+yVsYPXSFqPZNnRroccezim1MA42qyBpPaYIp3G/U8aq+MhB5BbV0EJSKQCJHpStsRYAMGbaEgUIA/lG65Ry0mnZMCIT2XmgEWyofNgOdCiuZwogZf5ulW8DB+ySZonnSmwfweQW1dBCUidEQcUIDlaMQqU2CRAUZAoOBom50OSXBAS1OSFC67fu47ZJ8kDl8vKrmNkd+sOcvzhaK9g/PhIA1nefYL0HPWVNFMhlft+bxQQaqgyMzP1KDjVuIcPH9bjEtz8cuXK2W4EiDGbpKQkPQjJxK2S7IOyFp6t+5wUKgsYw6EGjNJ+6zrq0hjQnDt3ri61Ke4YjO1QpsO+qHfjb8aKGHRlvkxp35egBSRqYo5Sdj4kOQXDreS8kItwq/HkC4XgKCS1XfvxR+TZYR/IvL5zFAyJsqLbOFnaY7KMevYd6Tjue51reALtRgDBMKjQPXfunK7DOnPmjB7ZPn/+vOtvZhAyqFfaD9ZfMThJnRelKBRNlmQflPX/9ttverTe+jk1YOz36tWrumGwrqOqmQaDyoOaNWsWewxeD5qRkSE//vijNt46daroyoFjx47puRv+nC/3HVtkANXbNkEJSIQCJDLxnNRTijaQqJ+FIMn3Jl7ByIfDm+H7oiY3AAij3xcuXNBzMhjRtnoLqnQpZ6esJBi8SGkAQt0Z81co66dy2HxO9QCek+Xzzz+XBg0auNbhWQDHHSpvKk1AqEi4dOmSvP322163CUpAwjUg2S5I8CaAYoWkfn5Ocj3kynXBUBIQXCB5AK4kgJjCQ2qsSuL6+Q5lJewHY/MGEa0gc00Iazp37ux1MhQFkUBKeEKrTQEjsw1NIWNRgBD6MI+FlhZv4O2cmYdCWQw1YxyHz/CihFDMY8GokZkTw/FnzZql4eGn2U+tWrX0sSi87Nq1a4EZkv4AwmxN7uG4ceP0fqzrKlWqpKccXLlyRde6URDK9u7XFJyADD8gdRQYeZBkK0iy88KtidkFwi2dl5geLi95SVEexVeVBJDFixfrB8/D8ed7GAcFjRQlWhe8DYZttgMEKoF/+umnAtsxtdf9pXwYm7X0HmPFKJivwn6BgwlW7oDExsbqOjHCJrMQHqalpWnD93T+XG92drbMnz9f/23CK6qY+ez06dOSlZWl10VGRuoiyu+++05PCwZWCjTxutYF4zdhkC+AUMP2yiuv6MlnZsFL7dq1S38fONatW1fgGNeuXdPzdcoEILUUIBETzmlI6mpQlCeZmAeJASXWQ15iPIkvoPjjZfwFhMSTUvODBw/q8MK6jodnyt+twkOwjhb44sWLOuSgPJ64nmJDjJr4HaNlPy+88IL+DMObMmWK9iIYLsbJrEHzZhQeMCEeBkRiS5UvRvzll1+6wAMOd0AoaiTxBQjOCS9GEeaKFSs0+KzzdO0UXAIU1cf8jeGzPfvmXtBRQRk/XgHgAZdqZrwPnorGgapkzoHiS7wNL70AImADquIAwXvhGQj1CKP43uzZs+Xs2bO6UQBEzgWvxXYcH0/t6aUaQQtI+PizGpA6Ewwk1z2Jy5u45yXak5zXgHiD5CE3D/JQMWEZ6/0FhNf70EJTZm5t9RFJuUnSrQthyNixY3VYhpE0adLE9R2TTDInnVCK3hvmt2P41glViLAEg0pJSdHg0WoDEhBZtyPcIjn2Bgj7JVQCTmvy3KJFCw0voSPG6n7tgMRUYnINjJCesZMnT+o5KPSUcR3kKeRgTBajMcBLme/jmayhDj1qeAOAYD5McYAwvRj4OXfr2zu5Bu4J99B4yDKbg4QN3S+1FSBA4u5Jrucl+Z7EDCgy8p58PXnXkHiBwx2SolQSQMyEKcIf64w+RPjD9dMSI0IiwinT1cmD5mUP7vvE0GiJmZeOZ8EIMJTo6OgC2/Hw8V5MrSXGBjZaSPcYnHnuGLo3QADNhFT0vBlhYHgIvBTewv08gZLOCea2YNjLly/XIJPAs56cgJaca3/55Zf1/iZPnqzX0bLj4XiRBccl7AF29nX8+HEdZhUHyCOPPKLPG89gPW/WE3LRo8j//yjTgIQqQGopOApAUgCU7AIhV0z+eEleeUqeJ9HdwJMLQ+LNk5R2DsJD5CExN6Oo7TCiX375RT98DIaH6endVrxogYcMICNHjpR9+/YVCwj7IdYuKSCAy3eZY+Iu1nvyIIjjkmvgSQiNOAZxP+sAhevl7S0fffSRDhGNl+UnQHAPGI/hntBo+AvI77//rkM3T+eNJy3zHqRa9+USNvqE1Bp3Jg8SBUdEvjzlJTH5YyV5I++5biFXwZzEW4jl7bPYobulcrj/vVCEDxgxXoTQwNvNp7UkHMFwmEiF4bz22ms6WTfbkQ8AD4ZF68eDxbgI4zp27Fhgn+QiGN3KlSt1DE+IhfGYniMjjIl8wBsgeClCI15X5O+1E1oR4uBpCO8WLlzoWkf4iXclaSYZJwwlHyGMxEsCFvmC2Z4wiQ4PXwHhmvl9z549Ohc0+6FTw70nsMwCUqlBPwkd/oPUVIC4PIkLkrMeIYm2gBI7yTLynmRN3q+HWEV5EOu62p3nSrmKJZuFN336dG3UPHQ8hYmJScbxAhgog4VmLMS81YQQhB4qHjBw0CLTEtOam1CF/ZGQv/XWWzon4TO6eWm1+Rxj4zMMndaUYzEXntyGBB7DLCpJp5uZVxJh6Jwf58z+CNvIITBKbz1ZGCk5EgsdFXzHrGM/eAcWQh7CMT7n+gktOXcGV/mMkItcCK/iKyDAxj7ZN5+zPXCQqNNgkbjTk8X+GY/B23CNnLOnqcgcXx1vcFABgqq2my2ho45pSAwoQFIwL8kuFG5Fu42VWMtTGvnSDWxRZN81UjHkxv7jLj1GJMOeFiscZnterEB3IwsJrOnuZR/W0IsHjgGz0ApiWOYnvTNmPIR9kyCb0XyzsL+iPIgBDoMyuQjna7qSPeUfRoRTL730kt6W/blXCuBh8B6EUuYlE+aa2DfXwfnRYeFvDqJtRwFA+Gi6d/HKLDQ8Jv9AwMQIflHdvOxT7f/pQAPyhy/GVbn5GAkdeUTCAGTc2cJ5SYHkPTtvQNFVw5VzPXm3hls+hFmNEk9JREKqVKhcOiUgeA5adN5yQgtPXkDIZB1ttopxCbpR6SkinyDk8NS6AQFvX6T7l65Runm9vf+X7mG6hmmd6dKkJgojAjLidowKb0Ndk3svEsYOPGyLV/Tlf7vggYj5PYWX7J+cCi/q/ipWzpNzxJNSv0ZvF16TXA6AAJ5WHUPn3PgbUFhv/ffh3Bs8JrkY4Sg9WNa3WrpsTHkpPBeNNufjvp6Q1Y53817y1bgIbypFd5UqrSZJ1dZTpGqbKfpnCFK/V2vzotIUqa5+Vo/P04NKNVDbvJ+hbadqhaF2U6VmEarRfIRUqFJ8TVBZEWEaQLgn6YBEnE4vlwk5HBVW/r9hC+w/81SAHLP7wm8VmRyEOJuuZAbuaFnJQQhjiirUc5TXLa0A6RBoQHbbfeG3ighBSPjpkbIuxPS8q9ck3448i9BY2WtsQAFRSc865/+jB1amWJHYnwTbl9JyR3m9hUuXLq0UUEAUkcmmu9KRo2CWstUziYmJtwcUkLS0tBh6OOy+eEeOihL/Y0UBkhFQOMyiDrzH6T1xFMyiK17ZaUO7AGmo4jufxkMcOQq0GCdSNrraFjjMopL16daRTUeOgkHUiyk4DmdlZd1nKyAkPxkZGfPpbrT7pjhyhBhQBY7MzMwQW+GwLoxUqsT9nLf/D+7I0c0WCXl+zrFK6V67mSi0pKam3qNAGatO7ltqcaj4pESbvnsKxhw5Ki2Zl1wwvYA6N2VzZ5fRXZWRUd9uDnxa1LnepTxKHXXCLVWe0lOpP8LTqJ8jHTnyV8qmnk3Pt6P0PFvqABAqlKqsco0/2W3zzuIst+Tyf4szjRG5g7eEAAAAAElFTkSuQmCC"
                      alt="Add to Google Wallet"
                      className="h-12 cursor-pointer hover:opacity-80 transition-opacity"
                    />
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>
      </>
    );
  }

  return (
    <div
      className="min-h-full bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8"
      style={{ backgroundColor: form.background_color, lineHeight: "1.15" }}
    >
      <div className="absolute top-4 right-4">
        <LanguageSelector textColor={form.text_color} />
      </div>
      <div className="max-w-md w-full space-y-8">
        {/* Company Logo */}
        {form.extras?.store_logo_url && (
          <div className="flex justify-center">
            <img
              src={form.extras.store_logo_url}
              alt={form.extras?.store_name}
              className="h-32 w-auto"
            />
          </div>
        )}

        {/* Form Description */}
        {form.description && (
          <p
            className="text-center"
            style={{ color: form.text_color }}
            dangerouslySetInnerHTML={{ __html: form.description }}
          ></p>
        )}

        {/* Error Message */}
        {error && (
          <div className="rounded-md bg-red-50 p-4">
            <div className="flex">
              <div className="shrink-0">
                <svg
                  className="h-5 w-5 text-red-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">{error}</h3>
              </div>
            </div>
          </div>
        )}

        {/* Form Fields */}
        <form onSubmit={handleSubmit} className="mt-8 space-y-6">
          {[
            // First, order fields according to fieldOrder
            ...form.content?.fields
              ?.filter((field) => fieldOrder.includes(field.field))
              .sort(
                (a, b) =>
                  fieldOrder.indexOf(a.field) - fieldOrder.indexOf(b.field),
              ),
            // Then, include any remaining non-marketing fields that aren't in fieldOrder
            ...form.content?.fields?.filter(
              (field) =>
                !fieldOrder.includes(field.field) &&
                field.field !== "marketing",
            ),
            // Finally, append the marketing field if it exists
            ...form.content?.fields?.filter(
              (field) => field.field === "marketing",
            ),
            // A hidden field's value comes from the query string, so there is
            // no input to draw. Rendering one left its label and the spacing
            // around it sitting mid-form above an invisible input.
          ]
            .filter((field) => !isHiddenFieldType(field.type))
            .map((field, index) => (
            <div key={index}>
              {field.field != "marketing" && (
                <label
                  htmlFor={`field-${index}`}
                  className="block text-sm font-medium"
                  style={{ color: form.text_color }}
                >
                  {field.label}
                  {field.required && (
                    <span className="text-red-500 ml-1">*</span>
                  )}
                </label>
              )}

              {field.description && (
                <p className="mt-1 text-sm" style={{ color: form.text_color, opacity: 0.7 }}>
                  {field.description}
                </p>
              )}
              <div className="mt-1">
                {field.type === "tel" ? (
                  <div className="flex gap-2">
                    <div className="w-64">
                      <Select
                        options={COUNTRY_OPTIONS}
                        key={isPhoneFieldReady ? "ready" : "loading"}
                        defaultValue={COUNTRY_OPTIONS.find((option) => {
                          if (countryData?.phone_code) {
                            if (countryData.phone_code === "1") {
                              return (
                                option.value ===
                                `+1 ${countryData.country_code}`
                              );
                            }
                            return (
                              option.value === `+${countryData.phone_code}`
                            );
                          }
                          return option.value === "+1 US";
                        })}
                        isLoading={!isPhoneFieldReady}
                        styles={customStyles}
                        isSearchable={true}
                        placeholder={t("public.form.countryPlaceholder")}
                        onChange={(option) => {
                          const phoneCode = option.value.includes(" ")
                            ? option.value.split(" ")[0]
                            : option.value;
                          handleInputChange(
                            field,
                            document.getElementById(`field-${index}`)?.value ||
                              "",
                            {
                              ...option,
                              value: phoneCode,
                            },
                          );
                        }}
                        formatOptionLabel={(option, { context }) => (
                          <div className="flex items-center gap-2">
                            <span>{option.label.split(" ")[0]}</span>
                            <span className="text-gray-500">
                              {option.value.split(" ")[0]}
                            </span>
                            {context === "menu" && (
                              <span className="truncate text-gray-500">
                                {getCountryName(option, language)}
                              </span>
                            )}
                          </div>
                        )}
                        filterOption={(option, inputValue) =>
                          filterCountryOption(option, inputValue, language)
                        }
                      />
                    </div>
                    <input
                      type="tel"
                      id={`field-${index}`}
                      //required={field.required} // Validation is handled in handleSubmit
                      placeholder={field.placeholder || "(555) 000-0000"}
                      autoComplete="tel-national"
                      value={formData[field.field] || ""}
                      onChange={(e) => {
                        const select = document
                          .querySelector(`#field-${index}`)
                          .previousSibling.querySelector(
                            '[class*="select__single-value"]',
                          );
                        const countryOption = COUNTRY_OPTIONS.find(
                          (opt) => opt.label === select?.textContent,
                        );
                        handleInputChange(field, e.target.value, countryOption);
                      }}
                      className="flex-1 rounded-md border-gray-300 shadow-xs placeholder-gray-400 sm:text-sm"
                      style={{ "--tw-ring-color": form.text_color, borderColor: "rgb(209 213 219)" }}
                      onFocus={(e) => { e.target.style.borderColor = form.text_color; e.target.style.boxShadow = `0 0 0 1px ${form.text_color}`; }}
                      onBlur={(e) => { e.target.style.borderColor = "rgb(209 213 219)"; e.target.style.boxShadow = "none"; }}
                    />
                  </div>
                ) : field.type === "select" ? (
                  <select
                    id={`field-${index}`}
                    //required={field.required} // Validation is handled in handleSubmit
                    value={formData[field.field] || ""}
                    onChange={(e) => handleInputChange(field, e.target.value)}
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-xs placeholder-gray-400 focus:outline-hidden sm:text-sm"
                    onFocus={(e) => { e.target.style.borderColor = form.text_color; e.target.style.boxShadow = `0 0 0 1px ${form.text_color}`; }}
                    onBlur={(e) => { e.target.style.borderColor = "rgb(209 213 219)"; e.target.style.boxShadow = "none"; }}
                  >
                    <option value="">
                      {field.placeholder ||
                        t("forms.new.fields.options.selectPlaceholder")}
                    </option>
                    {field.options?.map((option, optionIndex) => (
                      <option key={optionIndex} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                ) : field.type === "radio" ? (
                  <div className="mt-2 space-y-2">
                    {field.options?.map((option, optionIndex) => (
                      <div key={optionIndex} className="flex items-center">
                        <input
                          type="radio"
                          id={`field-${index}-${optionIndex}`}
                          name={`field-${index}`}
                          value={option}
                          checked={formData[field.field] === option}
                          //required={field.required} // Validation is handled in handleSubmit
                          onChange={(e) =>
                            handleInputChange(field, e.target.value)
                          }
                          className="h-4 w-4 border-gray-300"
                          style={{ accentColor: form.text_color }}
                        />
                        <label
                          htmlFor={`field-${index}-${optionIndex}`}
                          className="ml-3 block text-sm font-medium"
                          style={{ color: form.text_color }}
                        >
                          {option}
                        </label>
                      </div>
                    ))}
                  </div>
                ) : field.type === "checkbox" && field.field != "marketing" ? (
                  <div className="mt-2 space-y-2">
                    {field.options?.map((option, optionIndex) => (
                      <div key={optionIndex} className="flex items-center">
                        <input
                          type="checkbox"
                          id={`field-${index}-${optionIndex}`}
                          name={`field-${index}`}
                          value={option}
                          checked={formData[field.field]?.includes(option) || false}
                          onChange={(e) => {
                            const checkboxes = document.querySelectorAll(
                              `input[name="field-${index}"]:checked`,
                            );
                            const selectedValues = Array.from(checkboxes).map(
                              (cb) => cb.value,
                            );
                            handleInputChange(field, selectedValues);
                          }}
                          className="h-4 w-4 rounded-sm border-gray-300"
                          style={{ accentColor: form.text_color }}
                        />
                        <label
                          htmlFor={`field-${index}-${optionIndex}`}
                          className="ml-3 block text-sm font-medium"
                          style={{ color: form.text_color }}
                        >
                          {option}
                        </label>
                      </div>
                    ))}
                  </div>
                ) : field.field != "marketing" ? (
                  <input
                    type={field.type}
                    id={`field-${index}`}
                    //required={field.required} // Validation is handled in handleSubmit
                    placeholder={field.placeholder}
                    autoComplete={field.label}
                    value={formData[field.field] || ""}
                    onChange={(e) => handleInputChange(field, e.target.value)}
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-xs placeholder-gray-400 focus:outline-hidden sm:text-sm"
                    onFocus={(e) => { e.target.style.borderColor = form.text_color; e.target.style.boxShadow = `0 0 0 1px ${form.text_color}`; }}
                    onBlur={(e) => { e.target.style.borderColor = "rgb(209 213 219)"; e.target.style.boxShadow = "none"; }}
                  />
                ) : (
                  <div className="mt-2 space-y-2">
                    {field.options?.map((option, optionIndex) => (
                      <div key={optionIndex} className="flex items-center">
                        <input
                          type="checkbox"
                          id={`field-${index}-${optionIndex}`}
                          name={`field-${index}`}
                          value={option}
                          onChange={(e) => {
                            const checkboxes = document.querySelectorAll(
                              `input[name="field-${index}"]:checked`,
                            );
                            const selectedValues = Array.from(checkboxes).map(
                              (cb) => cb.value,
                            );
                            handleInputChange(field, selectedValues);
                          }}
                          className="h-4 w-4 rounded-sm border-gray-300"
                          style={{ accentColor: form.text_color }}
                        />
                        <label
                          htmlFor={`field-${index}-${optionIndex}`}
                          className="ml-3 block text-sm font-medium"
                          style={{ color: form.text_color }}
                        >
                          {option}
                        </label>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}

          <div className="mt-2 space-y-2">
            <div className="flex items-start">
              <div className="flex items-center h-5">
                <input
                  type="checkbox"
                  id="field-tos"
                  checked={acceptedTos}
                  onChange={(e) => {
                    setAcceptedTos(e.target.checked);
                  }}
                  className="h-4 w-4 rounded-sm border-gray-300"
                  style={{ accentColor: form.text_color }}
                />
              </div>
              <label
                className="ml-3 block text-sm font-medium"
                htmlFor="field-tos"
                style={{ color: form.text_color }}
              >
                {t("forms.tosAgreement", {
                  termsOfUseLink: (
                    <a
                      className="font-medium underline cursor-pointer"
                      style={{ color: form.text_color, opacity: 0.85 }}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();

                        if (form.extras?.custom_terms_of_use) {
                          openExternalUrl(form.extras.custom_terms_of_use, () =>
                            setIsTermsModalOpen(true),
                          );
                        } else {
                          setIsTermsModalOpen(true);
                        }
                      }}
                    >
                      {t("passes.form.termsOfService.label")}
                    </a>
                  ),
                })}
              </label>
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={
                submitMutation.isPending || isSubmitting || !acceptedTos
              }
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-xs text-sm font-medium focus:outline-hidden focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                backgroundColor: form.text_color,
                color: form.background_color,
              }}
            >
              {isSubmitting ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  {t("forms.public.creatingAccount")}
                </>
              ) : (
                form.submit_text || t("public.form.submitFallback")
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Terms of Service Modal */}
      <Transition appear show={isTermsModalOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-60"
          onClose={() => setIsTermsModalOpen(false)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-900/40" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="max-w-2xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 mb-4"
                  >
                    {t("passes.form.termsOfService.modalTitle")}
                  </Dialog.Title>
                  <div className="mt-2">
                    <div className="prose prose-sm prose-pre:bg-gray-100 prose-pre:text-gray-900 max-h-[60vh] overflow-y-auto">
                      <div
                        className="font-sans text-sm"
                        dangerouslySetInnerHTML={{
                          __html: getDefaultTermsOfService(
                            form.extras?.store_name,
                            form.extras?.support_email,
                          ),
                        }}
                      />
                    </div>
                  </div>
                  <div className="mt-4 flex justify-end">
                    <button
                      type="button"
                      className="inline-flex justify-center rounded-md border border-transparent px-4 py-2 text-sm font-medium"
                      style={{ backgroundColor: form.text_color, color: form.background_color }}
                      onClick={() => setIsTermsModalOpen(false)}
                    >
                      {t("common.close")}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
}
